import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import MentionTextarea, { renderMentionText } from '../components/MentionTextarea';
import { useAuth } from '../contexts/AuthContext';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { accionesDNAPI } from '../services/api';

// ─── Helpers de display ─────────────────────────────────────────────────────

const ESTADO_COLOR = {
  en_curso: 'success',
  cerrado: 'default',
};

const TIPO_COLOR = {
  comunicacion_marketing: 'info',
  workshop: 'secondary',
  contacto_negocio: 'primary',
  contacto_partners_universidades: 'warning',
  otro: 'default',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (dtStr) => {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (isNaN(d)) return dtStr;
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Formulario de acción ───────────────────────────────────────────────────

const EMPTY_FORM = {
  nombre: '',
  descripcion: '',
  estado: 'en_curso',
  tipo: '',
  frecuencia: '',
  fecha_puntual: '',
  periodicidad: '',
  periodicidad_texto: '',
  subnegocio: '',
  contacto_negocio: '',
  responsable_dh: '',
  deadline: '',
};

const AccionFormDialog = ({ open, onClose, onSaved, accion, choices }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accion) {
      setForm({
        nombre: accion.nombre || '',
        descripcion: accion.descripcion || '',
        estado: accion.estado || 'en_curso',
        tipo: accion.tipo || '',
        frecuencia: accion.frecuencia || '',
        fecha_puntual: accion.fecha_puntual || '',
        periodicidad: accion.periodicidad || '',
        periodicidad_texto: accion.periodicidad_texto || '',
        subnegocio: accion.subnegocio || '',
        contacto_negocio: accion.contacto_negocio || '',
        responsable_dh: accion.responsable_dh || '',
        deadline: accion.deadline || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [accion, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Si cambia la frecuencia, limpiar campos condicionales
      if (field === 'frecuencia') {
        if (value === 'puntual') {
          next.periodicidad = '';
          next.periodicidad_texto = '';
        } else {
          next.fecha_puntual = '';
        }
      }
      // Si periodicidad cambia a no "otra", limpiar texto libre
      if (field === 'periodicidad' && value !== 'otra') {
        next.periodicidad_texto = '';
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.nombre.trim()) { setError('El nombre de la acción es obligatorio.'); return; }
    if (!form.tipo) { setError('El tipo es obligatorio.'); return; }
    if (!form.frecuencia) { setError('La frecuencia es obligatoria.'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        subnegocio: form.subnegocio || null,
        responsable_dh: form.responsable_dh || null,
        fecha_puntual: form.fecha_puntual || null,
        deadline: form.deadline || null,
        periodicidad: form.periodicidad || null,
        periodicidad_texto: form.periodicidad_texto || null,
        contacto_negocio: form.contacto_negocio || null,
        descripcion: form.descripcion || null,
      };
      if (accion) {
        await accionesDNAPI.update(accion.id, payload);
      } else {
        await accionesDNAPI.create(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data
        ? JSON.stringify(err.response.data)
        : err.message || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const estadoOpts = choices?.estado || [];
  const tipoOpts = choices?.tipo || [];
  const frecuenciaOpts = choices?.frecuencia || [];
  const periodicidadOpts = choices?.periodicidad || [];
  const negociosOpts = choices?.negocios || [];
  const personasOpts = choices?.personas || [];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {accion ? 'Editar acción' : 'Nueva acción DN'}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          {/* Nombre */}
          <TextField
            label="Nombre de la acción *"
            value={form.nombre}
            onChange={handleChange('nombre')}
            fullWidth
            size="small"
            inputProps={{ maxLength: 255 }}
          />

          {/* Descripción */}
          <TextField
            label="Descripción"
            value={form.descripcion}
            onChange={handleChange('descripcion')}
            fullWidth
            multiline
            minRows={2}
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Estado */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Estado</InputLabel>
              <Select value={form.estado} label="Estado" onChange={handleChange('estado')}>
                {estadoOpts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tipo */}
            <FormControl size="small" sx={{ minWidth: 260 }} required>
              <InputLabel>Tipo *</InputLabel>
              <Select value={form.tipo} label="Tipo *" onChange={handleChange('tipo')}>
                <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                {tipoOpts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Frecuencia */}
            <FormControl size="small" sx={{ minWidth: 160 }} required>
              <InputLabel>Frecuencia *</InputLabel>
              <Select value={form.frecuencia} label="Frecuencia *" onChange={handleChange('frecuencia')}>
                <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                {frecuenciaOpts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Campos condicionales de frecuencia */}
          {form.frecuencia === 'puntual' && (
            <TextField
              label="Fecha de la acción"
              type="date"
              value={form.fecha_puntual}
              onChange={handleChange('fecha_puntual')}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 200 }}
            />
          )}
          {form.frecuencia === 'periodica' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Periodicidad</InputLabel>
                <Select value={form.periodicidad} label="Periodicidad" onChange={handleChange('periodicidad')}>
                  <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                  {periodicidadOpts.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {form.periodicidad === 'otra' && (
                <TextField
                  label="Especifica la periodicidad"
                  value={form.periodicidad_texto}
                  onChange={handleChange('periodicidad_texto')}
                  size="small"
                  sx={{ minWidth: 220 }}
                  inputProps={{ maxLength: 100 }}
                />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Subnegocio */}
            <FormControl size="small" sx={{ minWidth: 260, flex: 1 }}>
              <InputLabel>Subnegocio</InputLabel>
              <Select value={form.subnegocio} label="Subnegocio" onChange={handleChange('subnegocio')}>
                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                {[...negociosOpts]
                  .sort((a, b) => a.subnegocio.localeCompare(b.subnegocio, 'es'))
                  .map((n) => (
                    <MenuItem key={n.id} value={n.id}>
                      {n.subnegocio}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Responsable DH */}
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>Responsable de DH</InputLabel>
              <Select value={form.responsable_dh} label="Responsable de DH" onChange={handleChange('responsable_dh')}>
                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                {[...personasOpts]
                  .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Contacto de negocio */}
            <TextField
              label="Contacto de negocio"
              value={form.contacto_negocio}
              onChange={handleChange('contacto_negocio')}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              inputProps={{ maxLength: 255 }}
            />

            {/* Deadline */}
            <TextField
              label="Deadline"
              type="date"
              value={form.deadline}
              onChange={handleChange('deadline')}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 180 }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {accion ? 'Guardar cambios' : 'Crear acción'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Sección de comentarios ─────────────────────────────────────────────────

const ComentariosSection = ({ accionId, comentariosIniciales, currentUserName }) => {
  const [comentarios, setComentarios] = useState(comentariosIniciales || []);
  const [nuevoTexto, setNuevoTexto] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingComs, setLoadingComs] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTexto, setEditingTexto] = useState('');

  const cargarComentarios = useCallback(async () => {
    setLoadingComs(true);
    try {
      const res = await accionesDNAPI.getComentarios(accionId);
      setComentarios(res.data);
    } catch {
      // silent
    } finally {
      setLoadingComs(false);
    }
  }, [accionId]);

  useEffect(() => {
    cargarComentarios();
  }, [cargarComentarios]);

  const handleEnviar = async () => {
    if (!nuevoTexto.trim()) return;
    setSending(true);
    try {
      await accionesDNAPI.agregarComentario(accionId, { texto: nuevoTexto.trim() });
      setNuevoTexto('');
      await cargarComentarios();
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  const handleEliminar = async (comentarioId) => {
    if (!window.confirm('¿Eliminar este comentario?')) return;
    try {
      await accionesDNAPI.eliminarComentario(accionId, comentarioId);
      await cargarComentarios();
    } catch {
      // silent
    }
  };

  const handleGuardarEdicion = async (comentarioId) => {
    if (!editingTexto.trim()) return;
    try {
      const res = await accionesDNAPI.actualizarComentario(accionId, comentarioId, { texto: editingTexto });
      setComentarios(comentarios.map(c => c.id === comentarioId ? res.data : c));
      setEditingId(null);
      setEditingTexto('');
    } catch {
      // silent
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {loadingComs ? (
        <CircularProgress size={18} />
      ) : (
        <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
          {comentarios.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Sin comentarios todavía
            </Typography>
          ) : (
            comentarios.map((c) => (
              <Paper
                key={c.id}
                variant="outlined"
                sx={{ p: 1.2, borderRadius: 1.5, backgroundColor: '#fafafa', display: 'flex', gap: 1, alignItems: 'flex-start' }}
              >
                {/* Contenido del comentario */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {editingId === c.id ? (
                    <Box>
                      <textarea
                        style={{ width: '100%', minHeight: '70px', padding: '6px 8px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                        value={editingTexto}
                        onChange={(e) => setEditingTexto(e.target.value)}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Button size="small" variant="contained" onClick={() => handleGuardarEdicion(c.id)} disabled={!editingTexto.trim()}>Guardar</Button>
                        <Button size="small" variant="outlined" onClick={() => { setEditingId(null); setEditingTexto(''); }}>Cancelar</Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 0.3 }}>{renderMentionText(c.texto)}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {c.autor_nombre} · {formatDateTime(c.fecha_creacion)}
                  </Typography>
                </Box>
                {/* Acciones: solo para el autor */}
                {c.autor_nombre === currentUserName && editingId !== c.id && (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, flexShrink: 0, alignSelf: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => { setEditingId(c.id); setEditingTexto(c.texto); }}
                      sx={{ color: '#0d6efd', opacity: 0.7, '&:hover': { opacity: 1 } }}
                      title="Editar"
                    >
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEliminar(c.id)}
                      sx={{ color: '#dc3545', opacity: 0.7, '&:hover': { opacity: 1 } }}
                      title="Eliminar"
                    >
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            ))
          )}
        </Stack>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MentionTextarea
          value={nuevoTexto}
          onChange={(val) => setNuevoTexto(val)}
          rows={2}
          placeholder="Añadir comentario... Use @ para mencionar a alguien"
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleEnviar();
            }
          }}
          style={{
            width: '100%',
            padding: '8px 10px',
            fontSize: '13px',
            border: '1px solid #c4c4c4',
            borderRadius: '4px',
            resize: 'vertical',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <IconButton
          onClick={handleEnviar}
          disabled={sending || !nuevoTexto.trim()}
          color="primary"
          title="Enviar comentario (Enter)"
        >
          {sending ? <CircularProgress size={18} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Fila de tabla con comentarios expandibles ─────────────────────────────

const AccionRow = ({ accion, onEdit, onDelete, currentUserName }) => {
  const [expanded, setExpanded] = useState(false);

  const getFrecuenciaLabel = () => {
    if (accion.frecuencia === 'puntual') {
      return `Puntual${accion.fecha_puntual ? ` · ${formatDate(accion.fecha_puntual)}` : ''}`;
    }
    if (accion.frecuencia === 'periodica') {
      const pLabel = accion.periodicidad === 'otra'
        ? (accion.periodicidad_texto || 'Otra')
        : (accion.periodicidad_display || accion.periodicidad || '');
      return `Periódica${pLabel ? ` · ${pLabel}` : ''}`;
    }
    return accion.frecuencia_display || accion.frecuencia || '—';
  };

  const deadlineVencido = accion.deadline && new Date(accion.deadline) < new Date();

  const cellSx = { py: 1, px: 1.5, fontSize: '0.82rem', verticalAlign: 'top' };

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          borderLeft: `3px solid ${
            accion.estado === 'en_curso' ? '#198754' : '#adb5bd'
          }`,
          '&:hover': { backgroundColor: '#f8f9fa' },
        }}
      >
        {/* Expand */}
        <TableCell sx={{ ...cellSx, width: 36, pr: 0 }}>
          <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        {/* Nombre + descripción */}
        <TableCell sx={{ ...cellSx, cursor: 'pointer' }} onClick={() => setExpanded((p) => !p)}>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {accion.nombre}
          </Typography>
          {accion.descripcion && (
            <Typography variant="caption" color="text.secondary" title={accion.descripcion} sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {accion.descripcion}
            </Typography>
          )}
        </TableCell>

        {/* Estado */}
        <TableCell sx={{ ...cellSx, width: 100 }}>
          <Chip
            label={accion.estado_display || accion.estado}
            color={ESTADO_COLOR[accion.estado] || 'default'}
            size="small"
            sx={{ fontSize: '0.72rem', height: 22 }}
          />
        </TableCell>

        {/* Tipo */}
        <TableCell sx={{ ...cellSx, width: 200 }}>
          <Chip
            label={accion.tipo_display || accion.tipo}
            color={TIPO_COLOR[accion.tipo] || 'default'}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22 }}
          />
        </TableCell>

        {/* Frecuencia */}
        <TableCell sx={{ ...cellSx, width: 150 }}>
          <Typography variant="caption">{getFrecuenciaLabel()}</Typography>
        </TableCell>

        {/* Subnegocio */}
        <TableCell sx={{ ...cellSx, width: 150 }}>
          <Typography variant="caption">{accion.subnegocio_nombre || '—'}</Typography>
        </TableCell>

        {/* Responsable DH */}
        <TableCell sx={{ ...cellSx, width: 140 }}>
          <Typography variant="caption">{accion.responsable_dh_nombre || '—'}</Typography>
        </TableCell>

        {/* Contacto negocio */}
        <TableCell sx={{ ...cellSx, width: 140 }}>
          <Typography variant="caption">{accion.contacto_negocio || '—'}</Typography>
        </TableCell>

        {/* Deadline */}
        <TableCell sx={{ ...cellSx, width: 100 }}>
          <Typography
            variant="caption"
            sx={{ color: deadlineVencido ? '#dc3545' : 'inherit', fontWeight: deadlineVencido ? 600 : 400 }}
          >
            {formatDate(accion.deadline)}
          </Typography>
        </TableCell>

        {/* Creación */}
        <TableCell sx={{ ...cellSx, width: 100 }}>
          <Typography variant="caption">{formatDate(accion.created_at)}</Typography>
        </TableCell>

        {/* Acciones */}
        <TableCell sx={{ ...cellSx, width: 90, textAlign: 'right' }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEdit(accion)} sx={{ color: '#0d6efd' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" onClick={() => onDelete(accion)} sx={{ color: '#dc3545' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Fila expandida: descripción + comentarios */}
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, border: 0, backgroundColor: '#f8f9fa' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, px: 3 }}>
              {/* Descripción */}
              {accion.descripcion && (
                <Box sx={{ mb: 2, p: 1.2, backgroundColor: '#f0f0f0', borderRadius: 1.5 }}>
                  <Typography variant="body2">{accion.descripcion}</Typography>
                </Box>
              )}
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <CommentIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                Comentarios (más reciente primero)
              </Typography>
              <ComentariosSection accionId={accion.id} comentariosIniciales={[]} currentUserName={currentUserName} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Hook de ordenación ─────────────────────────────────────────────────────

const useSortedRows = (rows) => {
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  const sorted = [...rows].sort((a, b) => {
    let valA = a[orderBy] ?? '';
    let valB = b[orderBy] ?? '';
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return { sorted, orderBy, order, handleSort };
};

// ─── Componente principal AccionesDN ───────────────────────────────────────

const AccionesDN = () => {
  const { user } = useAuth();
  const currentUserName = user ? (user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.username) : '';
  const [acciones, setAcciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choices, setChoices] = useState(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroSubnegocio, setFiltroSubnegocio] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccion, setEditingAccion] = useState(null);

  const cargarAcciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await accionesDNAPI.getAll(params);
      setAcciones(res.data.results ?? res.data);
    } catch (err) {
      setError('Error al cargar las acciones. Por favor, inténtalo de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const cargarChoices = useCallback(async () => {
    try {
      const res = await accionesDNAPI.getChoices();
      setChoices(res.data);
    } catch (err) {
      console.error('Error al cargar opciones:', err);
    }
  }, []);

  useEffect(() => {
    cargarChoices();
  }, [cargarChoices]);

  useEffect(() => {
    const timer = setTimeout(() => {
      cargarAcciones();
    }, 300);
    return () => clearTimeout(timer);
  }, [cargarAcciones]);

  const handleNueva = () => {
    setEditingAccion(null);
    setDialogOpen(true);
  };

  const handleEdit = (accion) => {
    setEditingAccion(accion);
    setDialogOpen(true);
  };

  const handleDelete = async (accion) => {
    if (!window.confirm(`¿Eliminar la acción "${accion.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await accionesDNAPI.delete(accion.id);
      await cargarAcciones();
    } catch (err) {
      console.error('Error al eliminar:', err);
    }
  };

  const handleSaved = async () => {
    await cargarAcciones();
  };

  // Opciones únicas de subnegocio derivadas de las acciones cargadas
  const subnegocionOpts = [...new Map(
    acciones
      .filter((a) => a.subnegocio && a.subnegocio_nombre)
      .map((a) => [a.subnegocio, a.subnegocio_nombre])
  ).entries()]
    .map(([id, nombre]) => ({ id, nombre }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  // Filtrado local complementando búsqueda de texto en servidor
  const accionesFiltradas = acciones.filter((a) => {
    if (filtroEstado && a.estado !== filtroEstado) return false;
    if (filtroTipo && a.tipo !== filtroTipo) return false;
    if (filtroSubnegocio && String(a.subnegocio) !== String(filtroSubnegocio)) return false;
    return true;
  });

  const limpiarFiltros = () => {
    setSearch('');
    setFiltroEstado('');
    setFiltroTipo('');
    setFiltroSubnegocio('');
  };

  const hayFiltrosActivos = search || filtroEstado || filtroTipo || filtroSubnegocio;

  const { sorted: sortedAcciones, orderBy, order, handleSort } = useSortedRows(accionesFiltradas);

  return (
    <Box>
      {/* ── Barra de herramientas ── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          mb: 2,
          p: 1.5,
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #dee2e6',
        }}
      >
        {/* Búsqueda */}
        <TextField
          placeholder="Buscar acciones..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 220, flex: 1, maxWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filtro estado */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel shrink>Estado</InputLabel>
          <Select
            value={filtroEstado}
            label="Estado"
            displayEmpty
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {(choices?.estado || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro tipo */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel shrink>Tipo</InputLabel>
          <Select
            value={filtroTipo}
            label="Tipo"
            displayEmpty
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {(choices?.tipo || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro subnegocio */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel shrink>Subnegocio</InputLabel>
          <Select
            value={filtroSubnegocio}
            label="Subnegocio"
            displayEmpty
            onChange={(e) => setFiltroSubnegocio(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {subnegocionOpts.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Limpiar filtros */}
        {hayFiltrosActivos && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={limpiarFiltros}
            variant="outlined"
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Limpiar filtros
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Contador */}
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {accionesFiltradas.length} acción{accionesFiltradas.length !== 1 ? 'es' : ''}
        </Typography>

        {/* Nueva acción */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleNueva}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#adb5bd', color: '#495057', '&:hover': { borderColor: '#6c757d', backgroundColor: '#f8f9fa' } }}
        >
          Nueva acción
        </Button>
      </Box>

      {/* ── Contenido ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" action={
          <Button size="small" onClick={cargarAcciones}>Reintentar</Button>
        }>
          {error}
        </Alert>
      ) : accionesFiltradas.length === 0 ? (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <FilterListIcon sx={{ fontSize: 48, color: '#dee2e6', mb: 1 }} />
            <Typography color="text.secondary">
              {hayFiltrosActivos
                ? 'No hay acciones que coincidan con los filtros aplicados.'
                : 'Todavía no hay acciones registradas. ¡Crea la primera!'}
            </Typography>
            {hayFiltrosActivos && (
              <Button size="small" onClick={limpiarFiltros} sx={{ mt: 1 }}>
                Limpiar filtros
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f1f3f5', fontWeight: 700, fontSize: '0.78rem', py: 1, px: 1.5, whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ width: 36, pr: 0 }} />
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'nombre'}
                    direction={orderBy === 'nombre' ? order : 'asc'}
                    onClick={() => handleSort('nombre')}
                  >
                    Nombre
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 100 }}>Estado</TableCell>
                <TableCell sx={{ width: 200 }}>Tipo</TableCell>
                <TableCell sx={{ width: 150 }}>Frecuencia</TableCell>
                <TableCell sx={{ width: 150 }}>
                  <TableSortLabel
                    active={orderBy === 'subnegocio_nombre'}
                    direction={orderBy === 'subnegocio_nombre' ? order : 'asc'}
                    onClick={() => handleSort('subnegocio_nombre')}
                  >
                    Subnegocio
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  <TableSortLabel
                    active={orderBy === 'responsable_dh_nombre'}
                    direction={orderBy === 'responsable_dh_nombre' ? order : 'asc'}
                    onClick={() => handleSort('responsable_dh_nombre')}
                  >
                    Responsable DH
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 140 }}>Contacto negocio</TableCell>
                <TableCell sx={{ width: 100 }}>
                  <TableSortLabel
                    active={orderBy === 'deadline'}
                    direction={orderBy === 'deadline' ? order : 'asc'}
                    onClick={() => handleSort('deadline')}
                  >
                    Deadline
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 100 }}>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Creación
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 90, textAlign: 'right' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAcciones.map((accion) => (
                <AccionRow
                  key={accion.id}
                  accion={accion}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  currentUserName={currentUserName}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Dialog de formulario ── */}
      <AccionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
        accion={editingAccion}
        choices={choices}
      />
    </Box>
  );
};

export default AccionesDN;
